import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  STATIONS,
  dueDay,
  formatDateNoTime,
  formatHoursToHM,
  loadDashboardCards,
  materialAvailability,
} from "../../features/dashboard/dashboardData";
import { parseDueDate } from "../../utils/dueDate";
import "../mobile.css";

// The phone Dashboard (plan Phase 2): the desktop Dashboard's alert cards (P1 & P2, late, due today,
// needs review, consumables to reorder, materials short, pending RFQs) and today's hours per station.
// Read-only. The other charts (station timeline, leaderboards, pies) stay on the desktop page.
// Layout only: features/dashboard/dashboardData.js is the desktop page's own code.

const FIRST = 5;

function Section({ title, count, tone = "", testId, children, empty }) {
  const [all, setAll] = useState(false);
  const items = React.Children.toArray(children);
  const shown = all ? items : items.slice(0, FIRST);
  return (
    <section className="jq-card" data-testid={testId}>
      <h2 className="jq-section-title d-flex align-items-center mb-2">
        <span className="flex-grow-1">{title}</span>
        <span className={`jq-tag ${count ? tone : ""}`}>{count}</span>
      </h2>
      {items.length === 0 ? (
        <div className="jq-muted">{empty}</div>
      ) : (
        <>
          {shown}
          {!all && items.length > FIRST && (
            <button type="button" className="btn btn-outline-secondary jq-show-more mt-2" onClick={() => setAll(true)}>
              Show all ({items.length})
            </button>
          )}
        </>
      )}
    </section>
  );
}

function JobRow({ job, due = false }) {
  return (
    <Link to={`/jobs/${job.job_id}`} className="jq-task-line text-reset text-decoration-none">
      <span className="jq-task-line-main">
        <span className="fw-semibold">#{job.master_job_num}</span> {job.job_description}
        {due && <div className="jq-muted">Due {dueDay(job.due_date)}</div>}
      </span>
      <i className="fas fa-chevron-right jq-muted" aria-hidden="true" />
    </Link>
  );
}

export default function DashboardScreen() {
  const [cards, setCards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updated, setUpdated] = useState(null);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    try {
      const data = await loadDashboardCards();
      if (mine !== seq.current) return;
      setCards(data);
      setError(null);
      setUpdated(new Date());
    } catch (err) {
      // Keep the cards already shown: only the error is new.
      if (mine === seq.current) setError(err.message || "Couldn't load the dashboard.");
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => { seq.current += 1; };
  }, [load]);

  const refresh = (
    <div className="d-flex align-items-center gap-2 mb-2">
      <span className="jq-muted flex-grow-1">
        {updated ? `Updated ${updated.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : ""}
      </span>
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={load} disabled={loading}>
        <i className={`fas fa-sync-alt me-1 ${loading ? "fa-spin" : ""}`} aria-hidden="true" />Refresh
      </button>
    </div>
  );

  if (!cards) {
    return (
      <div className="jq-screen" data-testid="phone-dashboard">
        {refresh}
        {error ? (
          <div className="jq-note jq-note-danger" role="alert">{error}</div>
        ) : (
          <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading…</div>
        )}
      </div>
    );
  }

  const maxHours = Math.max(1, ...STATIONS.map((s) => cards.stationHoursToday[s] || 0));

  return (
    <div className="jq-screen" data-testid="phone-dashboard">
      {refresh}
      {error && <div className="jq-note jq-note-danger" role="alert">{error} Showing the last cards loaded.</div>}
      {cards.failed && cards.failed.length > 0 && (
        <div className="jq-note jq-note-danger" role="alert" data-testid="dash-failed">
          Couldn't load {cards.failed.join(", ")}: those cards may be missing items.
        </div>
      )}

      <Section title="Top priority (P1 & P2)" count={cards.priorityJobs.length} tone="jq-tag-warn" testId="dash-priority" empty="No top-priority jobs.">
        {cards.priorityJobs.map((j) => <JobRow key={j.job_id} job={j} />)}
      </Section>

      <Section title="Late jobs" count={cards.lateJobs.length} tone="jq-tag-block" testId="dash-late" empty="No late jobs.">
        {cards.lateJobs.map((j) => <JobRow key={j.job_id} job={j} due />)}
      </Section>

      <Section title="Due today" count={cards.dueTodayJobs.length} tone="jq-tag-next" testId="dash-due-today" empty="No jobs due today.">
        {cards.dueTodayJobs.map((j) => <JobRow key={j.job_id} job={j} />)}
      </Section>

      <Section
        title="Needs review"
        count={cards.flaggedParts.length + cards.flaggedCutSheets.length}
        tone="jq-tag-warn"
        testId="dash-review"
        empty="No flagged items."
      >
        {cards.flaggedParts.map((p) => (
          <div key={`part-${p.part_id}`} className="jq-task-line">
            <span className="jq-task-line-main">
              <span className="jq-tag me-1">Part</span>
              <span className="fw-semibold">{p.product_id}</span> {p.description}
              {p.flag_reason && <div className="jq-muted">{p.flag_reason}</div>}
            </span>
          </div>
        ))}
        {cards.flaggedCutSheets.map((c) => (
          <div key={`cs-${c.cut_sheet_id}`} className="jq-task-line">
            <span className="jq-task-line-main">
              <span className="jq-tag me-1">Cut sheet</span>
              <span className="fw-semibold">{c.cut_sheet_name || "Unnamed"}</span> {c.description}
              {c.flag_reason && <div className="jq-muted">{c.flag_reason}</div>}
            </span>
          </div>
        ))}
      </Section>

      <Section title="Consumables to reorder" count={cards.consumablesReorder.length} tone="jq-tag-warn" testId="dash-reorder" empty="No items need reordering.">
        {cards.consumablesReorder.map((c) => (
          <Link key={c.consumable_id} to={`/consumables?id=${c.consumable_id}`} className="jq-task-line text-reset text-decoration-none">
            <span className="jq-task-line-main">
              <span className="fw-semibold">{c.product_id}</span>
              <div className="jq-muted">On hand {c.stock_quantity ?? 0} · reorder at {c.reorder_point ?? 0}</div>
            </span>
            <i className="fas fa-chevron-right jq-muted" aria-hidden="true" />
          </Link>
        ))}
      </Section>

      <Section title="Materials short" count={cards.materialsNegative.length} tone="jq-tag-block" testId="dash-short" empty="No materials with negative availability.">
        {cards.materialsNegative.map((m) => (
          <div key={m.material_id} className="jq-task-line">
            <span className="jq-task-line-main">
              <span className="fw-semibold">{m.material_code || m.description}</span>
              <div className="jq-muted">
                On hand {m.stock_quantity || 0} · inbound {m.inbound_quantity || 0} · reserved {m.reserved_quantity || 0}
              </div>
            </span>
            <span className="fw-bold text-danger">{materialAvailability(m)}</span>
          </div>
        ))}
      </Section>

      <Section title="Pending RFQs" count={cards.pendingRfqs.length} testId="dash-rfqs" empty="No pending RFQs.">
        {cards.pendingRfqs.map((r) => (
          <div key={r.rfq_group_id} className="jq-task-line">
            <span className="jq-task-line-main">
              <span className="fw-semibold">{r.vendors.join(", ")}</span>
              <div className="jq-muted">
                {r.created_at ? formatDateNoTime(parseDueDate(r.created_at)) : "No date"} · {r.line_count} {r.line_count === 1 ? "item" : "items"}
              </div>
            </span>
          </div>
        ))}
      </Section>

      <section className="jq-card" data-testid="dash-station-hours">
        <h2 className="jq-section-title mb-2">Station hours today</h2>
        {STATIONS.map((s) => {
          const h = cards.stationHoursToday[s] || 0;
          return (
            <div key={s} className="d-flex align-items-center gap-2 mb-1">
              <span style={{ width: 92 }}>{s}</span>
              <span className="flex-grow-1" style={{ background: "var(--jq-bg-subtle, #f1f3f5)", borderRadius: 4, height: 12 }}>
                <span style={{ display: "block", width: `${(h / maxHours) * 100}%`, height: "100%", background: "var(--jq-ph-accent)", borderRadius: 4 }} />
              </span>
              <span className="jq-muted" style={{ width: 60, textAlign: "right" }}>{formatHoursToHM(h)}</span>
            </div>
          );
        })}
      </section>
    </div>
  );
}
